import { rpc } from "#rpc";
import { queryOptions } from "@tanstack/react-query";

// Types
type Markets = Record<string, { pricelists?: Record<string, { productsOnSale?: string[] }> }>;

export type Campaign = {
	campaign: string | number;
	name: string;
	markets: Markets;
	products: string[];
};

export type CampaignTagInfo = {
	id: string | number;
	name: string;
};

type RawCampaignData = {
	campaign: string | number;
	name: string;
	markets?: Markets;
};

// Query options
export const campaignsQueryOptions = queryOptions({
	queryKey: ["campaigns"],
	queryFn: async () => {
		const data = await rpc.campaigns.getCampaigns(true);
		return transformCampaignData(data);
	},
});

// Helper: Normalize raw data to array
function normalizeToArray(data: unknown): RawCampaignData[] {
	if (Array.isArray(data)) return data;

	if (data && typeof data === "object") {
		const keys = Object.keys(data);
		if (keys.length > 0 && keys.every((k) => !Number.isNaN(Number(k)))) {
			return Object.values(data);
		}
	}

	return [];
}

// Helper: Extract all products from campaign markets
function extractProductsFromMarkets(markets?: RawCampaignData["markets"]): string[] {
	if (!markets || typeof markets !== "object") return [];

	const products = new Set<string>();

	for (const market of Object.values(markets)) {
		const pricelists = market.pricelists;
		if (!pricelists || typeof pricelists !== "object") continue;

		for (const pricelist of Object.values(pricelists)) {
			const productsOnSale = pricelist.productsOnSale;
			if (Array.isArray(productsOnSale)) {
				productsOnSale.forEach((id) => products.add(id));
			}
		}
	}

	return Array.from(products);
}

// Transform raw campaign data
function transformCampaignData(rawData: unknown): Campaign[] {
	const campaignsArray = normalizeToArray(rawData);

	return campaignsArray.map((item) => ({
		campaign: item.campaign,
		name: item.name,
		markets: item.markets || {},
		products: extractProductsFromMarkets(item.markets),
	}));
}

// Get campaign tags for a product in a specific market and pricelist
export function getCampaignTagsForProduct(
	campaigns: Campaign[] | undefined,
	productId: string | undefined,
	productSku: string | undefined,
	marketId?: string,
	pricelistId?: string,
): CampaignTagInfo[] {
	if (!campaigns || (!productId && !productSku)) return [];

	return campaigns
		.filter((campaign) => {
			// If market and pricelist are provided, check specific market/pricelist
			if (marketId && pricelistId) {
				const productsInMarket = getProductsForMarketPricelist(campaign, marketId, pricelistId);
				return productsInMarket.includes(productId!) || productsInMarket.includes(productSku!);
			}

			// Fallback: check all products (all markets)
			if (!campaign.products.length) return false;
			return campaign.products.includes(productId!) || campaign.products.includes(productSku!);
		})
		.map((campaign) => ({
			id: campaign.campaign,
			name: campaign.name,
		}));
}

// Get products for specific market and pricelist
export function getProductsForMarketPricelist(
	campaign: Campaign | undefined,
	marketId: string,
	pricelistId: string,
): string[] {
	return campaign?.markets?.[marketId]?.pricelists?.[pricelistId]?.productsOnSale ?? [];
}
